import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../services/auth.service';
import { SessionService } from '../../services/session.service';

export type AuthMode = 'login' | 'signup';

/**
 * Shared modal for both Login (username + password) and Sign up
 * (email + password + activation code). The `mode` input toggles which
 * fields/labels are shown. For now submitting only logs the backend response.
 */
@Component({
    selector: 'app-auth-modal',
    imports: [FormsModule],
    templateUrl: './auth-modal.component.html',
    styleUrl: './auth-modal.component.scss',
})
export class AuthModalComponent {
    @Input() mode: AuthMode = 'login';

    username = '';
    email = '';
    password = '';
    activationCode = '';
    submitting = false;
    registered = false;
    errorMessage = '';

    constructor(
        public activeModal: NgbActiveModal,
        private authService: AuthService,
        private session: SessionService,
        private router: Router,
    ) { }

    get isLogin(): boolean {
        return this.mode === 'login';
    }

    switchMode(): void {
        this.mode = this.isLogin ? 'signup' : 'login';
        this.errorMessage = '';
    }

    submit(): void {
        if (this.submitting) return;
        this.submitting = true;
        this.errorMessage = '';
        const wasLogin = this.isLogin;

        const request$ = wasLogin
            ? this.authService.login(this.username, this.password)
            : this.authService.register(this.email, this.password, this.activationCode);

        request$.subscribe({
            next: (res) => {
                this.submitting = false;
                console.log(`[${wasLogin ? 'login' : 'signup'}] backend response:`, res);

                if (wasLogin) {
                    if (res?.token) {
                        this.session.setToken(res.token);
                        this.activeModal.close();
                        // Route by capability: dashboard staff (sales, and admins
                        // implicitly) go to the dashboard, a local file-portal user
                        // to Files, everyone else to gateways.
                        const isLocal = res.source === 'local';
                        const canAccessOrders = isLocal && (!!res.user?.isAdmin || !!res.user?.canAccessOrders);
                        const isLocalNonAdmin = isLocal && !res.user?.isAdmin;
                        this.router.navigate([
                            canAccessOrders ? '/dashboard' : isLocalNonAdmin ? '/my-files' : '/gateways',
                        ]);
                    } else {
                        // Reached the backend but no token issued -> bad credentials.
                        this.errorMessage = 'The email or password you entered is incorrect.';
                    }
                } else {
                    // Registration succeeded — swap the form for the
                    // "check your email to activate" confirmation.
                    this.registered = true;
                }
            },
            error: (err) => {
                this.submitting = false;
                console.error(`[${wasLogin ? 'login' : 'signup'}] backend error:`, err);
                this.errorMessage = wasLogin
                    ? 'The email or password you entered is incorrect.'
                    : 'We couldn\'t complete your sign up. Please check your details and try again.';
            },
        });
    }
}
